/* PJC v10 — effets étoilés légers, sans bibliothèque :
   - apparition au défilement (IntersectionObserver) : le contenu reste visible sans JS et n'est jamais masqué si l'observateur échoue ;
   - éclat étoilé bref au clic d'un bouton principal (un seul écouteur délégué) ;
   - pause des étoiles hors écran ;
   - <pjc-accordion> : enveloppe de <details> à hauteur animée (Web Animations API, 220 ms), utilisable sans JS.
   Pilotage : attributs data-pjc-fx-stars | reveal | sparkle = "on" | "off" posés sur <html> par layout/theme.liquid. */
(() => {
  'use strict';

  // Script inclus deux fois (éditeur, aperçu) : une seule initialisation.
  if (window.__pjcFxReady) return;
  window.__pjcFxReady = true;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fxOn = (name) => root.getAttribute('data-pjc-fx-' + name) === 'on';
  const inEditor = () => root.classList.contains('shopify-design-mode');

  /* ------------------------------------------------------------------ */
  /* Apparition au défilement                                            */
  /* ------------------------------------------------------------------ */
  const REVEAL_SELECTOR = '[data-pjc-reveal], [data-pjx-reveal]';
  const STATE = 'data-pjc-reveal-state';
  const seen = new WeakSet();
  let revealObserver = null;

  function createRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    try {
      // Seuil 0 + marge basse : se déclenche dès que le haut d'un bloc, même très haut, entre à l'écran.
      revealObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) reveal(entry.target);
          }
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0 }
      );
    } catch (error) {
      revealObserver = null;
    }
    return revealObserver;
  }

  function reveal(element) {
    revealObserver?.unobserve(element);
    if (element.getAttribute(STATE) !== 'hidden') return;
    element.setAttribute(STATE, 'shown');

    // Une fois la transition finie, on retire l'attribut : l'élément redevient un élément ordinaire.
    const cleanup = () => {
      element.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      if (element.getAttribute(STATE) === 'shown') element.removeAttribute(STATE);
    };
    const onEnd = (event) => {
      if (event.target === element) cleanup();
    };
    const timer = setTimeout(cleanup, 900);
    element.addEventListener('transitionend', onEnd);
  }

  function showAllHidden() {
    document.querySelectorAll('[' + STATE + '="hidden"]').forEach((element) => {
      revealObserver?.unobserve(element);
      element.removeAttribute(STATE);
    });
  }

  function prime(element) {
    if (seen.has(element)) return;
    seen.add(element);
    const rect = element.getBoundingClientRect();
    // Visible ou proche de l'écran, ou sans dimensions (replié, display:none) : on n'y touche pas.
    if (rect.height === 0 || rect.top < window.innerHeight * 0.92) return;
    element.setAttribute(STATE, 'hidden');
    revealObserver.observe(element);
  }

  function scanReveal(scope) {
    // Conditions strictes : réglage actif, mouvement non réduit, hors éditeur, observateur disponible.
    if (!fxOn('reveal') || reducedMotion.matches || inEditor() || !createRevealObserver()) return;
    try {
      const nodes = scope.matches?.(REVEAL_SELECTOR) ? [scope] : [];
      nodes.push(...scope.querySelectorAll(REVEAL_SELECTOR));
      nodes.forEach(prime);
    } catch (error) {
      showAllHidden();
    }
  }

  // Filet de sécurité : un bloc encore masqué alors qu'il est dans la fenêtre (observateur défaillant) est révélé.
  function sweep() {
    document.querySelectorAll('[' + STATE + '="hidden"]').forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) reveal(element);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Pause des étoiles hors écran                                         */
  /* ------------------------------------------------------------------ */
  let starObserver = null;
  const starsSeen = new WeakSet();

  function scanStars(scope) {
    if (!fxOn('stars') || reducedMotion.matches || !('IntersectionObserver' in window)) return;
    starObserver ||= new IntersectionObserver((entries) => {
      for (const entry of entries) entry.target.toggleAttribute('data-pjc-offscreen', !entry.isIntersecting);
    });
    const nodes = scope.matches?.('.pjc-stars') ? [scope] : [];
    nodes.push(...scope.querySelectorAll('.pjc-stars'));
    for (const node of nodes) {
      if (starsSeen.has(node)) continue;
      starsSeen.add(node);
      starObserver.observe(node);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Éclat étoilé au clic                                                 */
  /* ------------------------------------------------------------------ */
  const BURST_TARGET = '.button, .button-custom, [data-pjc-sparkle]';
  // Jamais sur : bouton secondaire, lien discret, paiement / validation de commande, état désactivé ou occupé.
  const BURST_EXCLUDED =
    '.button-secondary, .button-unstyled, [data-pjc-no-sparkle], [name="checkout"], a[href*="/checkout"], shopify-accelerated-checkout, .shopify-payment-button, [disabled], [aria-disabled="true"], [aria-busy="true"]';
  const BURST_COUNT = 6;
  const BURST_RADIUS = 36;

  function onDocumentClick(event) {
    if (!event.isTrusted || !fxOn('sparkle') || reducedMotion.matches || !(event.target instanceof Element)) return;

    const button = event.target.closest(BURST_TARGET);
    if (!button || button.matches(BURST_EXCLUDED) || button.closest(BURST_EXCLUDED)) return;
    if (document.querySelectorAll('.pjc-burst').length >= 2) return;

    const rect = button.getBoundingClientRect();
    // Clavier (clic synthétisé à 0,0) : l'éclat part du centre du bouton ; souris : du point cliqué.
    const fromPointer = event.clientX > 0 || event.clientY > 0;
    const x = fromPointer ? event.clientX : rect.left + rect.width / 2;
    const y = fromPointer ? event.clientY : rect.top + rect.height / 2;

    const layer = document.createElement('span');
    layer.className = 'pjc-burst';
    layer.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < BURST_COUNT; index += 1) {
      const star = document.createElement('i');
      const angle = (index / BURST_COUNT) * Math.PI * 2 - Math.PI / 2;
      star.style.setProperty('--bx', Math.round(Math.cos(angle) * BURST_RADIUS) + 'px');
      star.style.setProperty('--by', Math.round(Math.sin(angle) * BURST_RADIUS) + 'px');
      star.style.animationDelay = index % 2 ? '40ms' : '0ms';
      layer.append(star);
    }

    // Dans un <dialog> ouvert (tiroir, ajout rapide), le calque doit vivre dans la couche supérieure du dialogue.
    (button.closest('dialog[open]') || document.body).append(layer);
    layer.style.left = x + 'px';
    layer.style.top = y + 'px';
    // Si un ancêtre transformé change le repère « fixed », on recale d'après la position réellement mesurée.
    const measured = layer.getBoundingClientRect();
    if (Math.abs(measured.left - x) > 1 || Math.abs(measured.top - y) > 1) {
      layer.style.left = 2 * x - measured.left + 'px';
      layer.style.top = 2 * y - measured.top + 'px';
    }

    setTimeout(() => layer.remove(), 800);
  }

  /* ------------------------------------------------------------------ */
  /* <pjc-accordion>                                                     */
  /* ------------------------------------------------------------------ */
  const ACCORDION_MS = 220;
  const ACCORDION_EASING = 'cubic-bezier(0.22, 0.8, 0.3, 1)';

  /**
   * Enveloppe un ou plusieurs <details>. Sans JS ou en mouvement réduit : comportement natif.
   * Attribut facultatif `exclusive` : un seul volet ouvert à la fois.
   */
  class PjcAccordion extends HTMLElement {
    constructor() {
      super();
      this.running = new Map();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      for (const [details, state] of this.running) {
        state.animation.cancel();
        this.resetStyles(details);
        if (!state.opening) details.open = false;
      }
      this.running.clear();
    }

    onClick(event) {
      if (event.defaultPrevented || !(event.target instanceof Element)) return;
      const summary = event.target.closest('summary');
      const details = summary?.parentElement;
      if (!(details instanceof HTMLDetailsElement) || details.closest('pjc-accordion') !== this) return;

      // Lien ou bouton placé dans le résumé : on laisse faire.
      const inner = event.target.closest('a[href], button, input, select, textarea');
      if (inner && summary.contains(inner)) return;

      if (reducedMotion.matches || typeof details.animate !== 'function') return;

      event.preventDefault();
      this.toggle(details);
    }

    resetStyles(details) {
      details.style.height = '';
      details.style.overflow = '';
      details.style.boxSizing = '';
    }

    toggle(details) {
      const previous = this.running.get(details);
      const opening = previous ? !previous.opening : !details.open;
      const startHeight = details.getBoundingClientRect().height;

      if (previous) {
        previous.animation.cancel();
        this.running.delete(details);
      }
      this.resetStyles(details);

      if (opening && this.hasAttribute('exclusive')) {
        for (const other of this.querySelectorAll('details')) {
          const otherState = this.running.get(other);
          // Attribut natif name= : le navigateur ferme lui-même les volets du même groupe quand celui-ci s'ouvre.
          // Les animer aussi les ferait se chevaucher (et risquerait de les rouvrir) : on ne traite que les autres.
          if (details.name && other.name === details.name) continue;
          if (other !== details && other.closest('pjc-accordion') === this && (otherState ? otherState.opening : other.open)) {
            this.toggle(other);
          }
        }
      }

      let endHeight;
      if (opening) {
        details.open = true;
        endHeight = details.getBoundingClientRect().height;
      } else {
        details.open = false;
        endHeight = details.getBoundingClientRect().height;
        details.open = true;
      }

      details.style.overflow = 'hidden';
      details.style.boxSizing = 'border-box';
      const animation = details.animate([{ height: startHeight + 'px' }, { height: endHeight + 'px' }], {
        duration: ACCORDION_MS,
        easing: ACCORDION_EASING,
      });
      this.running.set(details, { animation, opening });

      animation.onfinish = () => {
        if (this.running.get(details)?.animation !== animation) return;
        this.running.delete(details);
        if (!opening) details.open = false;
        this.resetStyles(details);
      };
    }
  }

  if (!customElements.get('pjc-accordion')) customElements.define('pjc-accordion', PjcAccordion);

  /* ------------------------------------------------------------------ */
  /* Initialisation                                                       */
  /* ------------------------------------------------------------------ */
  function scan(scope) {
    scanReveal(scope);
    scanStars(scope);
  }

  document.addEventListener('click', onDocumentClick, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target);
  });
  window.addEventListener('beforeprint', showAllHidden);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) showAllHidden();
  });

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    showAllHidden();
    document.querySelectorAll('.pjc-burst').forEach((layer) => layer.remove());
  });

  scan(document);
  window.addEventListener('load', () => setTimeout(sweep, 3000), { once: true });
})();
